var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as R from 'ramda';
import { isEmpty, isNumber, px, pxWithAuto, setUnit, isFixedPosition } from './common';
import { FixedOutset, Unit, } from '../types/style';
export function mergeCommonStyle(commonStyle) {
    var size = commonStyle.size, transform = commonStyle.transform, text = commonStyle.text, border = commonStyle.border, background = commonStyle.background, margin = commonStyle.margin, padding = commonStyle.padding, zIndex = commonStyle.zIndex, cursor = commonStyle.cursor, restStyle = __rest(commonStyle, ["size", "transform", "text", "border", "background", "margin", "padding", "zIndex", "cursor"]);
    var style = restStyle;
    if (size) {
        if (!size.autoWidth) {
            style.width = setUnit(size.widthUnit, size.width);
        }
        else {
            style.maxWidth = '100%';
        }
        if (!size.autoHeight)
            style.height = setUnit(size.heightUnit, size.height);
    }
    if (transform) {
        var rotate = transform.rotate, opacity = transform.opacity, scale = transform.scale, radius = transform.radius;
        if ((rotate && rotate !== 0) || (scale && scale !== 1)) {
            style.transform = "" + (rotate ? "rotate(" + rotate + "deg) " : '') + (scale ? "scale(" + scale + ")" : '');
        }
        if (opacity && opacity < 1) {
            style.opacity = opacity;
        }
        if (radius && radius > 0) {
            style.borderRadius = radius + "px";
        }
    }
    if (margin) {
        var top_1 = margin.top, right = margin.right, bottom = margin.bottom, left = margin.left;
        if (top_1 !== 0 || right !== 0 || bottom !== 0 || left !== 0) {
            style.margin = [top_1, right, bottom, left].map(pxWithAuto).join(' ');
        }
    }
    if (padding) {
        var top_2 = padding.top, right = padding.right, bottom = padding.bottom, left = padding.left;
        if (top_2 !== 0 || right !== 0 || bottom !== 0 || left !== 0) {
            style.padding = [top_2, right, bottom, left].map(px).join(' ');
        }
    }
    if (typeof zIndex === 'number') {
        style.zIndex = zIndex;
    }
    if (typeof cursor === 'string') {
        style.cursor = cursor;
    }
    if (text) {
        style.color = text.color;
        style.fontSize = text.fontSize + "px";
        style.lineHeight = text.lineHeight + "px";
        style.textAlign = text.textAlign;
        style.fontWeight = text.weight;
    }
    if (border) {
        var type = border.type, color = border.color, width = border.width;
        if (width > 0) {
            style.border = width + "px " + type + " " + color;
        }
    }
    if (background) {
        var color = background.color, image = background.image, size_1 = background.size, repeat = background.repeat, position = background.position;
        style.backgroundColor = color;
        if (!isEmpty(image)) {
            style.backgroundImage = "url(" + image + ")";
        }
        style.backgroundRepeat = repeat;
        style.backgroundSize = size_1;
        style.backgroundPosition = position;
    }
    return style;
}
export var defaultDistance = { top: 0, left: 0, bottom: 0, right: 0 };
export var defaultCommonStyle = {
    size: {
        autoWidth: true,
        width: 200,
        autoHeight: true,
        height: 80,
    },
    transform: {
        rotate: 0,
        opacity: 1,
        scale: 1,
        radius: 0,
    },
    text: {
        color: '#161616',
        fontSize: 14,
        lineHeight: 20,
        textAlign: 'center',
        weight: 'normal',
    },
    border: {
        type: 'none',
        color: '#161616',
        width: 1,
    },
    background: {
        color: 'transparent',
        image: '',
        size: 'auto',
        position: 'center top',
        repeat: 'repeat-y',
    },
    margin: __assign(__assign({}, defaultDistance), { left: 'auto', right: 'auto' }),
    padding: defaultDistance,
    zIndex: true,
    position: true,
    cursor: 'default',
};
export function getPositionFromMeta(style) {
    var top = style.top, right = style.right;
    var outset = FixedOutset.BottomLeft;
    if (isNumber(top)) {
        outset = isNumber(right) ? FixedOutset.TopRight : FixedOutset.TopLeft;
    }
    else {
        outset = isNumber(right) ? FixedOutset.BottomRight : FixedOutset.BottomLeft;
    }
    return __assign(__assign(__assign({}, defaultDistance), style), { outset: outset });
}
export function getDefaultCommonStyle(styleGroup) {
    if (!styleGroup) {
        return {};
    }
    if (styleGroup === '*') {
        return defaultCommonStyle;
    }
    var style = {};
    R.mapObjIndexed(function (value, key) {
        if (key === 'zIndex') {
            if (typeof value === 'number') {
                style.zIndex = value;
            }
            else if (typeof value === 'boolean' && value) {
                style.zIndex = true;
            }
            return;
        }
        if (key === 'position') {
            if (typeof value === 'object') {
                style.position = getPositionFromMeta(value);
            }
            else if (typeof value === 'boolean' && value) {
                style.position = true;
            }
            return;
        }
        if ((key === 'margin' || key === 'padding') && typeof value === 'object') {
            style[key] = __assign(__assign({}, defaultDistance), value);
            return;
        }
        if (typeof value === 'boolean' && value) {
            style[key] = defaultCommonStyle[key];
            return;
        }
        if (typeof value === 'string' && value) {
            style[key] = value;
            return;
        }
        if (typeof value === 'object') {
            style[key] = __assign(__assign({}, defaultCommonStyle[key]), value);
        }
    }, styleGroup);
    return style;
}
export var defaultPageStyle = {
    margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    border: {
        type: 'none',
        color: '#161616',
        width: 1,
    },
    background: {
        color: '#ffffff',
        image: '',
        size: 'auto',
        position: 'center top',
        repeat: 'repeat-y',
    },
};
export function getStyleUnit(unit, value) {
    if (value === void 0) { value = 0; }
    switch (unit) {
        case undefined:
        case Unit.PX:
            return value.toFixed(0) + "px";
        case Unit.PERCENT:
            return value.toFixed(2) + "%";
        default:
    }
}
export function getPosition(commonStyle) {
    var position = commonStyle.position;
    if (!isFixedPosition(position)) {
        return null;
    }
    var style = {};
    var _a = position, outset = _a.outset, top = _a.top, left = _a.left, bottom = _a.bottom, right = _a.right, unit = _a.unit, type = _a.type;
    style.position = type === 'absolute' ? 'absolute' : 'fixed';
    var unitInfo = unit || {};
    switch (outset) {
        case 'TopLeft': {
            style.top = setUnit(unitInfo.top, top);
            style.left = setUnit(unitInfo.left, left);
            break;
        }
        case 'TopRight': {
            style.top = setUnit(unitInfo.top, top);
            style.right = setUnit(unitInfo.right, right);
            break;
        }
        case 'BottomRight': {
            style.bottom = setUnit(unitInfo.bottom, bottom);
            style.right = setUnit(unitInfo.right, right);
            break;
        }
        case 'BottomLeft': {
            style.bottom = setUnit(unitInfo.bottom, bottom);
            style.left = setUnit(unitInfo.left, left);
            break;
        }
        default:
            break;
    }
    return style;
}
//# sourceMappingURL=style.js.map